import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useBlog } from '../context/BlogContext';
import PageTransition from '../components/PageTransition';
import RichEditor from '../components/RichEditor';
import { uploadImage, deleteImage } from '../cloudinary';
import { motion } from 'framer-motion';
import ConfirmDialog from '../components/ConfirmDialog';

export default function AdminDashboard() {
  const { posts, loading, deletePost, siteSettings, updateSiteSettings, subscribers } = useBlog();

  /* Social links state */
  const [social, setSocial] = useState({ instagram: '', linkedin: '', twitter: '', email: '' });
  const [socialSaving, setSocialSaving] = useState(false);
  const [socialSaved, setSocialSaved] = useState(false);

  /* Page content state */
  const [homeContent, setHomeContent] = useState('');
  const [philContent, setPhilContent] = useState('');
  const [aboutContent, setAboutContent] = useState('');
  const [pageSaving, setPageSaving] = useState(false);
  const [pageSaved, setPageSaved] = useState(false);

  /* Brand logo state */
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [logoSaving, setLogoSaving] = useState(false);
  const [logoSaved, setLogoSaved] = useState(false);

  /* Active tab */
  const [tab, setTab] = useState('posts');
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  useEffect(() => {
    if (siteSettings) {
      setSocial(siteSettings.socialLinks || { instagram: '', linkedin: '', twitter: '', email: '' });
      setHomeContent(siteSettings.homeContent || '');
      setPhilContent(siteSettings.philosophyContent || '');
      setAboutContent(siteSettings.aboutContent || '');
      setLogoPreview(siteSettings.brandLogo || '');
    }
  }, [siteSettings]);

  const handleDelete = async (id) => {
    await deletePost(id);
    setDeleteTargetId(null);
  };

  const saveSocial = async () => {
    setSocialSaving(true);
    setSocialSaved(false);
    try {
      await updateSiteSettings({ socialLinks: social });
      setSocialSaved(true);
      setTimeout(() => setSocialSaved(false), 2000);
    } catch (e) { console.error(e); }
    finally { setSocialSaving(false); }
  };

  const savePages = async () => {
    setPageSaving(true);
    setPageSaved(false);
    try {
      await updateSiteSettings({ homeContent, philosophyContent: philContent, aboutContent });
      setPageSaved(true);
      setTimeout(() => setPageSaved(false), 2000);
    } catch (e) { console.error(e); }
    finally { setPageSaving(false); }
  };

  const saveLogo = async () => {
    if (!logoFile) return;
    setLogoSaving(true);
    setLogoSaved(false);
    try {
      // Delete old logo if exists
      if (siteSettings.brandLogoPublicId) {
        await deleteImage(siteSettings.brandLogoPublicId);
      }
      const result = await uploadImage(logoFile);
      await updateSiteSettings({ brandLogo: result.url, brandLogoPublicId: result.publicId });
      setLogoFile(null);
      setLogoSaved(true);
      setTimeout(() => setLogoSaved(false), 2000);
    } catch (e) { console.error(e); }
    finally { setLogoSaving(false); }
  };

  const [showLogoRemoveConfirm, setShowLogoRemoveConfirm] = useState(false);

  const removeLogo = async () => {
    setShowLogoRemoveConfirm(false);
    setLogoSaving(true);
    try {
      if (siteSettings.brandLogoPublicId) {
        await deleteImage(siteSettings.brandLogoPublicId);
      }
      await updateSiteSettings({ brandLogo: '', brandLogoPublicId: '' });
      setLogoPreview('');
      setLogoFile(null);
    } catch (e) { console.error(e); }
    finally { setLogoSaving(false); }
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files[0] || null;
    setLogoFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setLogoPreview(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const tabStyle = (active) => ({
    padding: '0.45rem 1.1rem', borderRadius: '8px', fontSize: '0.85rem',
    fontFamily: 'var(--font-sans)', cursor: 'pointer',
    border: '1px solid', transition: 'all 0.2s',
    background: active ? 'var(--text-primary)' : 'var(--bg-sidebar)',
    color: active ? 'var(--bg-body)' : 'var(--text-primary)',
    borderColor: active ? 'var(--text-primary)' : 'var(--border-subtle)',
  });

  const labelStyle = { fontSize: '0.85rem', color: 'var(--text-tertiary)', display: 'block', marginBottom: '0.4rem' };
  const sectionTitle = { fontFamily: 'var(--font-serif)', fontSize: '1.2rem', fontWeight: 400, marginBottom: '1.25rem' };

  return (
    <PageTransition>
      <div className="container" style={{ paddingTop: '3rem', paddingBottom: '4rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.8rem', fontWeight: 400 }}>Dashboard</h2>
          <Link to="/admin/new" className="btn btn-primary">+ New Post</Link>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
          <button onClick={() => setTab('posts')} style={tabStyle(tab === 'posts')}>Posts</button>
          <button onClick={() => setTab('social')} style={tabStyle(tab === 'social')}>Social Links</button>
          <button onClick={() => setTab('pages')} style={tabStyle(tab === 'pages')}>Page Content</button>
          <button onClick={() => setTab('brand')} style={tabStyle(tab === 'brand')}>Brand Logo</button>
          <button onClick={() => setTab('subscribers')} style={tabStyle(tab === 'subscribers')}>Subscribers{subscribers.length > 0 ? ` (${subscribers.length})` : ''}</button>
        </div>

        {/* Posts Tab */}
        <ConfirmDialog
          open={!!deleteTargetId}
          title="Delete post"
          message="This post and all its comments will be permanently removed."
          confirmLabel="Delete"
          onConfirm={() => handleDelete(deleteTargetId)}
          onCancel={() => setDeleteTargetId(null)}
        />

        {tab === 'posts' && (
          <>
            {loading ? (
              <p style={{ color: 'var(--text-tertiary)', fontSize: '0.9rem' }}>Loading posts...</p>
            ) : posts.length === 0 ? (
              <p style={{ color: 'var(--text-tertiary)', fontSize: '0.9rem', fontStyle: 'italic' }}>No posts yet.</p>
            ) : (
              posts.map((p, i) => (
                <motion.div key={p.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: i * 0.05 }}
                  className="admin-post-row"
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    background: 'var(--bg-card)', padding: '1rem 1.25rem', borderRadius: 'var(--radius-sm)',
                    marginBottom: '0.5rem', border: '1px solid var(--border-light)',
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, gap: '1rem' }}>
                    {p.image ? (
                      <img src={p.image} alt="" style={{ width: '48px', height: '48px', borderRadius: '6px', objectFit: 'cover', flexShrink: 0 }} />
                    ) : (
                      <div style={{ width: '48px', height: '48px', borderRadius: '6px', flexShrink: 0, background: 'var(--bg-body)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-tertiary)', fontSize: '0.65rem' }}>No img</div>
                    )}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '0.95rem', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.title}</div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)' }}>{p.date} - {p.comments.length} comments</div>
                    </div>
                  </div>
                  <div className="admin-post-actions" style={{ display: 'flex', gap: '0.4rem', flexShrink: 0, marginLeft: '1rem' }}>
                    <Link to={`/post/${p.id}`} className="btn" style={{ padding: '0.3rem 0.7rem', fontSize: '0.75rem' }}>View</Link>
                    <Link to={`/admin/edit/${p.id}`} className="btn" style={{ padding: '0.3rem 0.7rem', fontSize: '0.75rem' }}>Edit</Link>
                    <button onClick={() => setDeleteTargetId(p.id)} className="btn btn-danger" style={{ padding: '0.3rem 0.7rem', fontSize: '0.75rem' }}>Delete</button>
                  </div>
                </motion.div>
              ))
            )}
          </>
        )}

        {/* Social Links Tab */}
        {tab === 'social' && (
          <div style={{ maxWidth: '480px' }}>
            <h3 style={sectionTitle}>Social Links</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={labelStyle}>Instagram URL</label>
                <input className="form-input" value={social.instagram} onChange={(e) => setSocial({ ...social, instagram: e.target.value })} placeholder="https://instagram.com/..." />
              </div>
              <div>
                <label style={labelStyle}>LinkedIn URL</label>
                <input className="form-input" value={social.linkedin} onChange={(e) => setSocial({ ...social, linkedin: e.target.value })} placeholder="https://linkedin.com/company/..." />
              </div>
              <div>
                <label style={labelStyle}>Twitter / X URL</label>
                <input className="form-input" value={social.twitter} onChange={(e) => setSocial({ ...social, twitter: e.target.value })} placeholder="https://x.com/..." />
              </div>
              <div>
                <label style={labelStyle}>Email</label>
                <input className="form-input" value={social.email} onChange={(e) => setSocial({ ...social, email: e.target.value })} placeholder="hello@umi.com" />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <button className="btn btn-primary" onClick={saveSocial} disabled={socialSaving}>
                  {socialSaving ? 'Saving...' : 'Save Links'}
                </button>
                {socialSaved && <span style={{ fontSize: '0.82rem', color: 'var(--accent-warm)' }}>Saved</span>}
              </div>
            </div>
          </div>
        )}

        {/* Page Content Tab */}
        {tab === 'pages' && (
          <div>
            <h3 style={sectionTitle}>Homepage Content</h3>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-tertiary)', marginBottom: '0.75rem' }}>
              This replaces the default hero and "How UMI Thinks" sections on the homepage. Leave empty to use defaults.
            </p>
            <RichEditor value={homeContent} onChange={setHomeContent} placeholder="Custom homepage content..." style={{ marginBottom: '2.5rem' }} />

            <h3 style={sectionTitle}>Philosophy Page Content</h3>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-tertiary)', marginBottom: '0.75rem' }}>
              This replaces the entire philosophy page content. Leave empty to use defaults.
            </p>
            <RichEditor value={philContent} onChange={setPhilContent} placeholder="Custom philosophy content..." style={{ marginBottom: '1.5rem' }} />

            <h3 style={sectionTitle}>About Us Page Content</h3>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-tertiary)', marginBottom: '0.75rem' }}>
              This replaces the default About Us page content. Leave empty to use defaults.
            </p>
            <RichEditor value={aboutContent} onChange={setAboutContent} placeholder="Custom about us content..." style={{ marginBottom: '1.5rem' }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <button className="btn btn-primary" onClick={savePages} disabled={pageSaving}>
                {pageSaving ? 'Saving...' : 'Save Page Content'}
              </button>
              {pageSaved && <span style={{ fontSize: '0.82rem', color: 'var(--accent-warm)' }}>Saved</span>}
            </div>
          </div>
        )}

        {/* Brand Logo Tab */}
        {tab === 'brand' && (
          <div style={{ maxWidth: '480px' }}>
            <h3 style={sectionTitle}>Brand Logo</h3>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-tertiary)', marginBottom: '1.25rem' }}>
              Upload your brand logo. It will appear in the sidebar header.
            </p>

            <ConfirmDialog
              open={showLogoRemoveConfirm}
              title="Remove logo"
              message="The brand logo will be removed from the sidebar."
              confirmLabel="Remove"
              onConfirm={removeLogo}
              onCancel={() => setShowLogoRemoveConfirm(false)}
            />

            {logoPreview && (
              <div style={{ marginBottom: '1.25rem', display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                <img src={logoPreview} alt="Brand logo" style={{ maxWidth: '120px', maxHeight: '120px', borderRadius: '8px', objectFit: 'contain', border: '1px solid var(--border-subtle)', padding: '0.5rem', background: 'var(--bg-card)' }} />
                {siteSettings.brandLogo && !logoFile && (
                  <button className="btn btn-danger" style={{ padding: '0.3rem 0.75rem', fontSize: '0.8rem' }} onClick={() => setShowLogoRemoveConfirm(true)} disabled={logoSaving}>
                    Remove
                  </button>
                )}
              </div>
            )}

            <div style={{ marginBottom: '1.25rem' }}>
              <input type="file" accept="image/*" onChange={handleLogoFileChange} style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }} />
            </div>

            {logoFile && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <button className="btn btn-primary" onClick={saveLogo} disabled={logoSaving}>
                  {logoSaving ? 'Uploading...' : 'Upload Logo'}
                </button>
                <button className="btn" onClick={() => { setLogoFile(null); setLogoPreview(siteSettings.brandLogo || ''); }}>
                  Cancel
                </button>
                {logoSaved && <span style={{ fontSize: '0.82rem', color: 'var(--accent-warm)' }}>Saved</span>}
              </div>
            )}
          </div>
        )}

        {/* Subscribers Tab */}
        {tab === 'subscribers' && (
          <div>
            <h3 style={sectionTitle}>Newsletter Subscribers</h3>
            {subscribers.length === 0 ? (
              <p style={{ fontSize: '0.9rem', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>No subscribers yet.</p>
            ) : (
              <>
                <p style={{ fontSize: '0.82rem', color: 'var(--text-tertiary)', marginBottom: '1rem' }}>
                  {subscribers.length} subscriber{subscribers.length !== 1 ? 's' : ''}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                  {subscribers.map((s, i) => (
                    <motion.div key={s.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.25, delay: i * 0.03 }}
                      style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        background: 'var(--bg-card)', padding: '0.75rem 1rem', borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--border-light)',
                      }}>
                      <span style={{ fontSize: '0.9rem' }}>{s.email}</span>
                      <span style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>
                        {s.subscribedAt?.toDate?.().toLocaleDateString() || '-'}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </PageTransition>
  );
}
